import { Box, Button, Divider, VStack } from '@chakra-ui/react'
import { Form, Formik } from 'formik'
import _, { isNull, isString, isUndefined } from 'lodash'
import moment from 'moment'
import type { ReactElement } from 'react'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { CachePolicies, useFetch } from 'use-http'
import * as Yup from 'yup'
import { checkOperationPermission, PermissionsEnum } from '../../../../../utils'
import { ACLPermissionEnum, CmFieldTypeEnum } from '../../../../constants'
import useToast from '../../../../hooks/useToast'
import type {
  CmErrorObject,
  CmFieldResponseObject,
  FolderDetailsResponse,
  MediaCollectionDetailsResponse,
  MediaLibrarySearchCollectionResponse,
  MediaLibrarySearchFolderResponse,
} from '../../../../interfaces'
import type {
  CmFormValues,
  FileDetailsResponse,
} from '../../../../interfaces/FileDetailsResponse'
import { UserContext } from '../../../../providers'
import { CustomMetadataContext } from '../../../../providers/CustomMetadataProvider'
import PermissionGuard from '../../../PermissionGuard'
import CMBooleanField from './CMBooleanField'
import CMDateField from './CMDateField'
import CMMultiSelectField from './CMMultiSelectField'
import CMNumberField from './CMNumberField'
import CMSingleSelectField from './CMSingleSelectField'
import CMTextareaField from './CMTextareaField'
import CMTextField from './CMTextField'
import {getCmFieldValidatorMap, validator} from  '../../NewButton/uploadUtils'

const CMFormField = ({
  cmField,
  disabled,
}: {
  cmField: CmFieldResponseObject
  disabled: boolean
}): React.ReactElement => {

  switch (cmField.schema.type) {
    case CmFieldTypeEnum.Boolean:
      return <CMBooleanField cmField={cmField} disabled={disabled} />
    case CmFieldTypeEnum.Number:
      return <CMNumberField cmField={cmField} disabled={disabled} />
    case CmFieldTypeEnum.Text:
      return <CMTextField cmField={cmField} disabled={disabled} validator={validator} />
    case CmFieldTypeEnum.Textarea:
      return <CMTextareaField cmField={cmField} disabled={disabled} validator={validator}/>
    case CmFieldTypeEnum.SingleSelect:
      return <CMSingleSelectField cmField={cmField} disabled={disabled} />
    case CmFieldTypeEnum.MultiSelect:
      return <CMMultiSelectField cmField={cmField} disabled={disabled} />
    case CmFieldTypeEnum.Date:
      return <CMDateField cmField={cmField} disabled={disabled} />
    default:
      throw Error('Should not happen')
  }
}

type CustomMetadataFormProps =
  | {
      fileDetails: FileDetailsResponse
      folder?: undefined
      folderDetails?: undefined
      mediaCollection?: undefined
      mediaCollectionDetails?: undefined
    }
  | {
      fileDetails?: undefined
      folder: MediaLibrarySearchFolderResponse
      folderDetails?: FolderDetailsResponse
      mediaCollection?: undefined
      mediaCollectionDetails?: undefined
    }
  | {
      fileDetails?: undefined
      folder?: undefined
      folderDetails?: undefined
      mediaCollection: MediaLibrarySearchCollectionResponse
      mediaCollectionDetails?: MediaCollectionDetailsResponse
    }

const CustomMetadataForm = ({
  fileDetails,
  folder,
  folderDetails,
  mediaCollection,
  mediaCollectionDetails,
}: CustomMetadataFormProps): ReactElement => {
  const customMetadata =
    fileDetails?.customMetadata ??
    folderDetails?.customMetadata ??
    mediaCollectionDetails?.customMetadata
  const { t } = useTranslation()
  const toast = useToast()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
    data: [],
  })
  const [initialState, setInitialState] = useState<CmFormValues>(
    customMetadata ?? {},
  )
  const { cmFields = [] } = useContext(CustomMetadataContext)
  const { user } = useContext(UserContext)

  const cmFieldValidatorMap = getCmFieldValidatorMap(cmFields)

  const validate = async (formValues: CmFormValues) => {
    const fieldEntries = Object.entries(formValues)
      .filter(
        ([fieldName]) => cmFieldValidatorMap.has(fieldName), // if value belongs to deleted metadata
      )
      .map(([fieldName, value]) => {
        if (isNull(value)) {
          return [fieldName, undefined]
        }
        return [fieldName, value]
      }) as Array<[string, unknown]>

    const fieldErrors = {}

    // Check all mandatory fields are added
    cmFields
      .filter(field => field.schema.isValueRequired)
      .forEach(field => {
        if (
          isUndefined(formValues[field.name]) ||
          isNull(formValues[field.name])
        ) {
          fieldErrors[field.name] = 'Mandatory field'
        }
      })

    // Validate each field & populate fieldErrors
    await Promise.allSettled(
      fieldEntries.map(([fieldName, value]) =>
        cmFieldValidatorMap
          .get(fieldName)
          ?.validate(value)
          .then(validValue => [fieldName, validValue] as [string, unknown])
          .catch(error => {
            fieldErrors[fieldName] = error.message
          }),
      ),
    )

    return fieldErrors
  }

  const onSubmit = async (formValues: CmFormValues, { setFieldError }) => {
    const fieldEntries = Object.entries(formValues).filter(
      ([fieldName]) => cmFieldValidatorMap.has(fieldName), // if value belongs to deleted metadata
    )

    const fieldsToDelete = {}
    fieldEntries
      .filter(
        ([fieldName, value]) =>
          isNull(value) && !isUndefined(initialState[fieldName]),
      )
      .forEach(([fieldName]) => {
        fieldsToDelete[fieldName] = null
      })

    const fieldsToUpdate = {}
    fieldEntries
      .filter(
        ([fieldName, value]) =>
          JSON.stringify(value) !== JSON.stringify(initialState[fieldName]),
      )
      .forEach(([fieldName, value]) => {
        fieldsToDelete[fieldName] = value
      })

    const customMetadataPatch = { ...fieldsToDelete, ...fieldsToUpdate }

    let endpoint: string
    if (fileDetails) {
      endpoint = `/api/v2/files/${fileDetails.fileId}/details`
    } else if (folder) {
      endpoint = `/api/v2/folder/details?folderId=${folder.folderId}`
    } else {
      endpoint = `/api/v2/media-collections/${mediaCollection.mediaCollectionId}/details`
    }
    const resp = (await request.patch(endpoint, {
      customMetadata: customMetadataPatch,
    })) as CmFieldResponseObject | CmErrorObject
    if (request.error) {
      let fieldErrors = (resp as CmErrorObject).errors ?? {}
      if (Array.isArray(fieldErrors)) {
        fieldErrors = Object.fromEntries(
          (
            fieldErrors.filter(
              ({ field, error }) => isString(field) && isString(error),
            ) as Array<{ field: string, error: string }>
          ).map(({ field, error }) => [field, error]),
        )
      }
      let fieldErrorPresent = false
      if (Object.entries(fieldErrors).length) {
        Object.entries(fieldErrors).forEach(([key, value]) => {
          setFieldError(key, value)
        })
        fieldErrorPresent = true
      }
      if (!fieldErrorPresent) {
        toast({
          description: (resp as CmErrorObject).message,
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      }
      return
    }
    setInitialState({
      ...initialState,
      ...customMetadataPatch,
    })
    toast({
      title: t(
        'custom_metadata_tags_updated_successfully',
        'Custom metadata updated successfully',
      ),
      status: 'success',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })
  }

  const operationPermission = checkOperationPermission({
    user,
    aclPermission: [ACLPermissionEnum.CONTRIBUTE, ACLPermissionEnum.MANAGE],
    selectedFiles: fileDetails ? [fileDetails] : [],
    selectedFolders: folder ? [folder] : [],
    selectedMediaCollections: mediaCollection ? [mediaCollection] : [],
  })

  return (
    <Box position="relative" width="100%" height="100%">
      {cmFields.length ? (
        <Formik<CmFormValues>
          initialValues={initialState}
          enableReinitialize={true}
          validate={validate}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, isValid, dirty }) => (
            <PermissionGuard
              permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
            >
              {(hasMlManagePermission: boolean) => (
                <VStack
                  as={Form}
                  justifyContent="space-between"
                  spacing="0"
                  width="100%"
                  height="100%"
                >
                  <PermissionGuard
                    permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
                  >
                    {(hasImageSettingsManagePermission: boolean) =>
                      hasImageSettingsManagePermission ? (
                        <Box
                          width="full"
                          backgroundColor="appWhite"
                          flexShrink="0"
                        >
                          <Button
                            as={Link}
                            to="/settings/media-library"
                            variant="solid"
                            isFullWidth
                          >
                            +{t('add_new', 'Add New')}
                          </Button>

                          <Divider
                            color="appGrey"
                            width="auto"
                            mt="1em"
                            ml="-1em"
                            mr="-1em"
                          />
                        </Box>
                      ) : (
                        <></>
                      )
                    }
                  </PermissionGuard>

                  <VStack
                    width="full"
                    spacing="1.5em"
                    flex-grow="1"
                    alignItems="flex-start"
                    overflowY="auto"
                    py="4"
                    justifyContent="flex-start"
                    height="full"
                  >
                    {cmFields.map(cmField => (
                      <CMFormField
                        key={cmField.id}
                        cmField={cmField}
                        disabled={
                          !(hasMlManagePermission && operationPermission)
                        }
                      />
                    ))}
                  </VStack>

                  {hasMlManagePermission && operationPermission ? (
                    <Box flexShrink="0" width="full" backgroundColor="appWhite">
                      <Divider
                        color="appGrey"
                        width="auto"
                        mb="1em"
                        ml="-1em"
                        mr="-1em"
                      />

                      <Button
                        type="submit"
                        isFullWidth
                        isLoading={isSubmitting}
                        disabled={!(isValid && dirty)}
                      >
                        {t('save', 'Save')}
                      </Button>
                    </Box>
                  ) : (
                    <></>
                  )}
                </VStack>
              )}
            </PermissionGuard>
          )}
        </Formik>
      ) : (
        <VStack
          justifyContent="space-between"
          spacing="0"
          width="100%"
          height="100%"
        >
          <PermissionGuard
            permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
          >
            {(hasImageSettingsManagePermission: boolean) =>
              hasImageSettingsManagePermission ? (
                <Box width="full" backgroundColor="appWhite" flexShrink="0">
                  <Button
                    as={Link}
                    to="/settings/media-library"
                    variant="solid"
                    isFullWidth
                  >
                    +{t('add_new', 'Add New')}
                  </Button>

                  <Divider
                    color="appGrey"
                    width="auto"
                    mt="1em"
                    ml="-1em"
                    mr="-1em"
                  />
                </Box>
              ) : (
                <></>
              )
            }
          </PermissionGuard>
        </VStack>
      )}
    </Box>
  )
}

export default CustomMetadataForm

export interface CMFieldProps {
  cmField: CmFieldResponseObject
  disabled?: boolean
  isUpload?: boolean
  validator?:Function
}
